<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

interface Props {
  solarRadiation: number | null | undefined;
  loading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  loading: false
});

// 格式化數值
const formattedValue = computed(() => {
  if (props.solarRadiation === null || props.solarRadiation === undefined) return '0';
  return props.solarRadiation.toLocaleString('zh-TW', { maximumFractionDigits: 1 });
});

// 計算日照等級
const radiationLevel = computed(() => {
  const value = props.solarRadiation;
  if (value === null || value === undefined || value === 0) return 'none';
  if (value < 200) return 'low';
  if (value < 600) return 'medium';
  if (value < 900) return 'high';
  return 'very-high';
});

// 計算日照等級文字
const radiationLevelText = computed(() => {
  switch (radiationLevel.value) {
    case 'low': return t('dashboard.weather.radiationLow');
    case 'medium': return t('dashboard.weather.radiationMedium');
    case 'high': return t('dashboard.weather.radiationHigh');
    case 'very-high': return t('dashboard.weather.radiationVeryHigh');
    default: return '-';
  }
});
</script>

<template>
  <div class="power-card solar-card">
    <div class="card-header">
      <div class="card-icon">☀️</div>
      <h3>{{ t('dashboard.weather.solarRadiation') }}</h3>
    </div>
    <div class="card-value">
      {{ formattedValue }}<span class="unit">W/m²</span>
    </div>
    <div v-if="solarRadiation !== null && solarRadiation !== undefined && solarRadiation > 0"
         class="efficiency"
         :class="'level-' + radiationLevel">
      {{ radiationLevelText }}
    </div>
    <div class="card-footer">Solar Irradiance</div>
  </div>
</template>

<style scoped>
.power-card {
  background: rgba(255, 255, 255, 0.08);
  padding: 25px;
  border-radius: 10px;
  border: 2px solid transparent;
  transition: all 0.3s;
  cursor: pointer;
}

.power-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
}

.solar-card {
  border-color: #FF9800;
}

.solar-card:hover {
  background: rgba(255, 152, 0, 0.1);
  box-shadow: 0 10px 25px rgba(255, 152, 0, 0.3);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 15px;
}

.card-icon {
  font-size: 32px;
}

.card-header h3 {
  font-size: 16px;
  color: #b0bec5;
  font-weight: 600;
  margin: 0;
}

.card-value {
  font-size: 48px;
  font-weight: bold;
  color: #ecf0f1;
  line-height: 1.2;
}

.card-value .unit {
  font-size: 18px;
  font-weight: normal;
  color: #90a4ae;
  margin-left: 8px;
}

.efficiency {
  font-size: 14px;
  padding: 6px 12px;
  border-radius: 6px;
  text-align: center;
  margin-top: 10px;
  display: inline-block;
}

.level-low {
  background: rgba(33, 150, 243, 0.2);
  color: #2196F3;
}

.level-medium {
  background: rgba(255, 193, 7, 0.2);
  color: #FFC107;
}

.level-high {
  background: rgba(255, 152, 0, 0.2);
  color: #FF9800;
}

.level-very-high {
  background: rgba(244, 67, 54, 0.2);
  color: #F44336;
}

.card-footer {
  font-size: 12px;
  color: #78909c;
  margin-top: 10px;
  text-transform: uppercase;
  letter-spacing: 1px;
}
</style>
